import React, { useEffect, useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { blogPosts, BlogPost } from '@/lib/blogData';
import { Button } from '@/components/ui/button';
import MarkdownRenderer from '@/components/blog/MarkdownRenderer';
import BlogPostSection from '@/components/blog/BlogPostSection';
import { BlogPostHeader } from '@/components/blog/post/BlogPostHeader';
import { BlogPostFooter } from '@/components/blog/post/BlogPostFooter';
import { RelatedPosts } from '@/components/blog/post/RelatedPosts';
import SEO from '@/components/seo/SEO';

const BlogPostPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<BlogPost | null>(null);

  useEffect(() => {
    const foundPost = blogPosts.find(p => p.slug === slug);
    setPost(foundPost || null);
    window.scrollTo(0, 0);
  }, [slug]);

  const { intro, sections, readTime } = useMemo(() => {
    if (!post) return { intro: '', sections: [], readTime: 0 };
    
    const wordCount = post.contentMarkdown.split(/\s+/).length;
    const time = Math.ceil(wordCount / 200); 

    const safeParts = post.contentMarkdown.split('## ').map((part, i) => i === 0 ? part : `## ${part}`);
    
    return { intro: safeParts[0], sections: safeParts.slice(1), readTime: time };
  }, [post]);

  if (!post) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <h1 className="text-2xl font-bold mb-4">Article not found</h1>
        <Button asChild><Link to="/blog">Return to Blog</Link></Button>
      </div>
    );
  }

  // Use default site image if post has no image, otherwise use post image
  const ogImage = post.imageUrl;
  const siteUrl = import.meta.env.VITE_SITE_URL || import.meta.env.VITE_APP_URL || 'https://lendlifted.com';

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `${siteUrl}/blog/${post.slug}`
    },
    "headline": post.title,
    "image": ogImage ? [ogImage] : [`${siteUrl}/og-image.png`],
    "datePublished": post.date,
    "dateModified": post.date, // Assuming no separate modified date yet
    "author": {
      "@type": "Person",
      "name": post.author || "LendLifted Team"
    },
    "publisher": {
      "@type": "Organization",
      "name": "LendLifted",
      "logo": {
        "@type": "ImageObject",
        "url": `${siteUrl}/assets/logo.svg`
      }
    },
    "description": post.excerpt
  };

  return (
    <div className="min-h-screen bg-background font-sans selection:bg-primary/20">
      <SEO 
        title={post.title}
        description={post.excerpt}
        canonical={`/blog/${post.slug}`}
        ogType="article"
        ogImage={ogImage}
        articlePublishedTime={post.date}
        articleAuthor={post.author}
        articleSection={post.category}
        articleTags={post.tags}
        jsonLd={jsonLd}
      />

      <article>
        <BlogPostHeader post={post} readTime={readTime} />

        {/* Main Content Area */}
        <div className="container px-4 py-12 md:py-20 max-w-3xl mx-auto">
            {post.imageUrl && (
                <div className="relative -mt-20 md:-mt-32 mb-16 rounded-2xl overflow-hidden shadow-2xl border border-border bg-card">
                    <img src={post.imageUrl} alt={post.title} className="w-full h-auto object-cover aspect-[21/9]" />
                </div>
            )}

            {/* Introduction */}
            <div className="mb-12">
                <MarkdownRenderer content={intro} />
            </div>

            {/* Sections */}
            {sections.map((section, idx) => (
                <BlogPostSection key={idx}>
                    <MarkdownRenderer content={section} />
                </BlogPostSection>
            ))}

            <BlogPostFooter tags={post.tags} />
        </div>
      </article>

      <RelatedPosts currentSlug={post.slug} />
      
    </div>
  );
};

export default BlogPostPage;
